/**
 * Media Service - Frontend API client for the canonical media endpoints
 * 
 * This service provides a unified interface to the provider-neutral media API,
 * while also providing backward compatibility adapters for existing components.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Media types supported by the canonical API
 */
export const MediaType = {
  TV: 'tv',
  MOVIE: 'movie',
  BOOK: 'book',
  PODCAST: 'podcast'
};

/**
 * Search media items using the canonical API
 * @param {string} query - Search query
 * @param {string} type - Media type (tv, movie, book, podcast)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<{items: Array, totalResults: number, totalPages: number, cached: boolean}>}
 */
export async function searchMedia(query, type, page = 1, limit = 24) {
  const params = { q: query, page, limit };
  if (type) params.type = type;
  
  const response = await axios.get(`${API}/media/search`, { params });
  return {
    items: response.data.items,
    totalResults: response.data.total_results,
    totalPages: response.data.total_pages,
    cached: response.data.cached,
    provider: response.data.provider
  };
}

/**
 * Get trending/popular media items
 * @param {string} type - Media type (tv, movie, book, podcast)
 * @param {number} page - Page number
 * @param {string} timeWindow - Time window (day, week)
 * @returns {Promise<{items: Array, totalResults: number, totalPages: number}>}
 */
export async function getTrending(type, page = 1, timeWindow = 'week') {
  const response = await axios.get(`${API}/media/trending`, {
    params: { type, page, time_window: timeWindow }
  });
  return {
    items: response.data.items,
    totalResults: response.data.total_results,
    totalPages: response.data.total_pages
  };
}

/**
 * Discover media items with filters
 * @param {string} type - Media type
 * @param {object} options - Filter options
 * @returns {Promise<{items: Array, totalResults: number, totalPages: number}>}
 */
export async function discoverMedia(type, options = {}) {
  const params = {
    type,
    page: options.page || 1,
    limit: options.limit || 24,
    ...(options.genre && { genre: options.genre }),
    ...(options.year && { year: options.year }),
    ...(options.fictionFilter && { fiction_filter: options.fictionFilter }),
    ...(options.orderBy && { order_by: options.orderBy }),
    ...(options.englishOnly && { english_only: options.englishOnly }),
    ...(options.minRating && { min_rating: options.minRating })
  };
  
  const response = await axios.get(`${API}/media/discover`, { params });
  return {
    items: response.data.items,
    totalResults: response.data.total_results,
    totalPages: response.data.total_pages
  };
}

/**
 * Get detailed information about a media item
 * @param {string} canonicalId - Canonical media ID (e.g., "tmdb_movie_123", "gb_abc123")
 * @returns {Promise<object>} Media details
 */
export async function getMediaDetails(canonicalId) {
  const response = await axios.get(`${API}/media/${canonicalId}`);
  return response.data;
}

/**
 * Look up media by provider-specific external ID
 * @param {string} provider - Provider name (tmdb, google_books, listen_notes)
 * @param {string} externalId - Provider-specific ID
 * @param {string} type - Media type
 * @returns {Promise<object>} Media item
 */
export async function lookupByExternalId(provider, externalId, type) {
  const response = await axios.get(`${API}/media/lookup/${provider}/${externalId}`, {
    params: { type }
  });
  return response.data;
}

/**
 * Get genres for a media type
 * @param {string} type - Media type
 * @returns {Promise<Array<{id: string|number, name: string}>>}
 */
export async function getGenres(type) {
  const response = await axios.get(`${API}/media/genres/${type}`);
  return response.data.genres;
}

// ============== Backward Compatibility Adapters ==============

/**
 * Convert canonical MediaItem to TMDB-style format for TV/Movies
 * This allows existing components to work with canonical data
 */
export function toTMDBFormat(mediaItem) {
  if (!mediaItem) return null;
  
  const tmdbId = mediaItem.external_ids?.tmdb_id;
  
  return {
    id: tmdbId || mediaItem.id,
    tmdb_id: tmdbId,
    canonical_id: mediaItem.id,
    name: mediaItem.title,
    title: mediaItem.title,
    overview: mediaItem.description,
    poster_path: mediaItem.cover_image,
    backdrop_path: mediaItem.backdrop_image,
    vote_average: mediaItem.rating ? mediaItem.rating : 0,
    vote_count: mediaItem.rating_count || 0,
    first_air_date: mediaItem.release_date,
    release_date: mediaItem.release_date,
    genre_ids: [], // Would need genre ID mapping
    genres: mediaItem.genres?.map(g => ({ name: g })) || [],
    popularity: mediaItem.popularity,
    // TV specific
    number_of_seasons: mediaItem.season_count,
    number_of_episodes: mediaItem.episode_count,
    status: mediaItem.status,
    // Preserve all creators
    created_by: mediaItem.creators?.filter(c => c.role === 'creator').map(c => ({ name: c.name })) || [],
    // Provider info
    _provider: mediaItem.provider,
    _canonical: mediaItem
  };
}

/**
 * Convert canonical MediaDetails to TMDB-style format for SeriesDetailsDialog
 * Includes cast, watch_providers, and other detail-specific fields
 */
export function toTMDBDetailsFormat(mediaDetails) {
  if (!mediaDetails) return null;
  
  const baseFormat = toTMDBFormat(mediaDetails);
  const isMovie = mediaDetails.type === 'movie';
  
  // Extract directors from creators
  const directors = mediaDetails.creators
    ?.filter(c => c.role === 'director')
    .map(c => c.name) || [];
  
  // Extract networks/production companies from creators
  const networks = mediaDetails.creators
    ?.filter(c => c.role === 'network')
    .map(c => c.name) || [];
  
  const productionCompanies = mediaDetails.creators
    ?.filter(c => c.role === 'production')
    .map(c => c.name) || [];
  
  // Convert cast to expected format
  const cast = mediaDetails.cast?.map(member => ({
    id: member.id || member.name,
    name: member.name,
    character: member.role !== 'actor' ? member.role : '',
    profile_path: member.image_url || member.image  // Backend uses image_url
  })) || [];
  
  // Convert watch providers
  const watchProviders = {};
  if (mediaDetails.watch_providers) {
    if (mediaDetails.watch_providers.flatrate) {
      watchProviders.flatrate = mediaDetails.watch_providers.flatrate.map(p => ({
        provider_id: p.provider_id,
        name: p.provider_name || p.name,
        logo: p.logo_path || p.logo
      }));
    }
    if (mediaDetails.watch_providers.buy) {
      watchProviders.buy = mediaDetails.watch_providers.buy.map(p => ({
        provider_id: p.provider_id,
        name: p.provider_name || p.name,
        logo: p.logo_path || p.logo
      }));
    }
    if (mediaDetails.watch_providers.rent) {
      watchProviders.rent = mediaDetails.watch_providers.rent.map(p => ({
        provider_id: p.provider_id,
        name: p.provider_name || p.name,
        logo: p.logo_path || p.logo
      }));
    }
    if (mediaDetails.watch_providers.link) {
      watchProviders.link = mediaDetails.watch_providers.link;
    }
  }
  
  // Build IMDb URL if available
  const imdbUrl = mediaDetails.external_ids?.imdb_id 
    ? `https://www.imdb.com/title/${mediaDetails.external_ids.imdb_id}`
    : null;
  
  return {
    ...baseFormat,
    media_type: mediaDetails.type,
    tagline: mediaDetails.tagline,
    overview: mediaDetails.description,
    // Dates
    first_air_date: mediaDetails.release_date,
    last_air_date: mediaDetails.end_date,
    // Ratings
    vote_average: mediaDetails.rating || 0,
    vote_count: mediaDetails.rating_count || 0,
    // Runtime for movies
    runtime: mediaDetails.runtime ? `${mediaDetails.runtime} min` : null,
    // TV specific
    number_of_seasons: mediaDetails.season_count,
    number_of_episodes: mediaDetails.episode_count,
    // Credits
    directors,
    networks,
    production_companies: productionCompanies,
    cast,
    // Watch providers
    watch_providers: watchProviders,
    // External links
    imdb_url: imdbUrl,
    // Genres as strings
    genres: mediaDetails.genres || []
  };
}

/**
 * Fetch media details and convert to provider format
 * @param {string} canonicalId - Canonical media ID
 * @returns {Promise<object>} Details in provider format
 */
export async function getMediaDetailsFormatted(canonicalId) {
  const details = await getMediaDetails(canonicalId);
  return toTMDBDetailsFormat(details);
}

/**
 * Fetch media details by TMDB ID (for backward compatibility)
 * @param {string|number} tmdbId - TMDB ID
 * @param {string} mediaType - 'tv' or 'movie'
 * @returns {Promise<object>} Details in TMDB format
 */
export async function getTMDBDetails(tmdbId, mediaType) {
  const canonicalId = `tmdb_${mediaType}_${tmdbId}`;
  return getMediaDetailsFormatted(canonicalId);
}

/**
 * Convert canonical MediaDetails to Google Books/Hardcover-style detailed format
 * Includes similar books and additional metadata
 */
export function toGoogleBooksDetailsFormat(mediaDetails) {
  if (!mediaDetails) return null;
  
  const baseFormat = toGoogleBooksFormat(mediaDetails);
  
  // Convert similar books
  const similar = mediaDetails.similar?.map(item => toGoogleBooksFormat(item)) || [];
  
  return {
    ...baseFormat,
    // Additional detail fields (supports both Hardcover and Google Books)
    preview_link: mediaDetails.provider_data?.preview_link,
    info_link: mediaDetails.provider_data?.info_link,
    hardcover_url: mediaDetails.provider_data?.hardcover_url,
    edition_count: mediaDetails.provider_data?.edition_count,
    language: mediaDetails.provider_data?.language,
    // Series info (from Hardcover)
    series: mediaDetails.provider_data?.series || null,
    // Similar books
    similar_books: similar,
    // ISBNs
    isbn_10: mediaDetails.external_ids?.isbn_10,
    isbn_13: mediaDetails.external_ids?.isbn_13
  };
}

/**
 * Fetch book details by book ID (supports both Hardcover and Google Books IDs)
 * @param {string} bookId - Hardcover or Google Books volume ID
 * @returns {Promise<object>} Details in book format
 */
export async function getBookDetails(bookId) {
  // Try Hardcover first, then Google Books format for legacy support
  let canonicalId = bookId.startsWith('hc_') || bookId.startsWith('gb_') 
    ? bookId 
    : `hc_${bookId}`;
  
  try {
    const details = await getMediaDetails(canonicalId);
    return toGoogleBooksDetailsFormat(details);
  } catch (error) {
    // If Hardcover fails and it's not already a gb_ ID, try as Google Books
    if (!bookId.startsWith('gb_') && !bookId.startsWith('hc_')) {
      try {
        const gbDetails = await getMediaDetails(`gb_${bookId}`);
        return toGoogleBooksDetailsFormat(gbDetails);
      } catch (e) {
        throw error; // Throw original error
      }
    }
    throw error;
  }
}

/**
 * Convert canonical MediaDetails to Listen Notes-style detailed format
 * Includes recommendations and additional metadata
 */
export function toListenNotesDetailsFormat(mediaDetails) {
  if (!mediaDetails) return null;
  
  const baseFormat = toListenNotesFormat(mediaDetails);
  
  // Convert similar/recommended podcasts
  const recommendations = mediaDetails.similar?.map(item => toListenNotesFormat(item)) || [];
  
  return {
    ...baseFormat,
    // Additional detail fields
    explicit_content: mediaDetails.provider_data?.explicit_content,
    latest_episode_id: mediaDetails.provider_data?.latest_episode_id,
    latest_pub_date_ms: mediaDetails.provider_data?.latest_pub_date_ms,
    // Recommendations
    recommendations
  };
}

/**
 * Fetch podcast details by Listen Notes ID
 * @param {string} listenNotesId - Listen Notes podcast ID
 * @returns {Promise<object>} Details in Listen Notes format
 */
export async function getPodcastDetails(listenNotesId) {
  const canonicalId = `ln_${listenNotesId}`;
  const details = await getMediaDetails(canonicalId);
  return toListenNotesDetailsFormat(details);
}

/**
 * Convert canonical MediaItem to Google Books/Hardcover-style format
 */
export function toGoogleBooksFormat(mediaItem) {
  if (!mediaItem) return null;
  
  // Support both Hardcover and legacy Google Books IDs
  const hcId = mediaItem.external_ids?.hardcover_id;
  const gbId = mediaItem.external_ids?.google_books_id;
  const bookId = hcId || gbId || mediaItem.id;
  
  return {
    id: bookId,
    book_id: bookId,
    hardcover_id: hcId,
    google_books_id: gbId,
    canonical_id: mediaItem.id,
    title: mediaItem.title,
    subtitle: mediaItem.subtitle,
    author: mediaItem.creators?.filter(c => c.role === 'author').map(c => c.name).join(', ') || '',
    authors: mediaItem.creators?.filter(c => c.role === 'author').map(c => c.name) || [],
    description: mediaItem.description,
    cover_image: mediaItem.cover_image,
    thumbnail: mediaItem.cover_image,
    categories: mediaItem.genres || [],
    genres: mediaItem.genres || [],
    page_count: mediaItem.page_count,
    published_date: mediaItem.release_date,
    publisher: mediaItem.publisher,
    average_rating: mediaItem.rating,
    rating: mediaItem.rating,
    ratings_count: mediaItem.rating_count,
    // Hardcover-specific
    slug: mediaItem.provider_data?.slug,
    hardcover_url: mediaItem.provider_data?.hardcover_url,
    users_count: mediaItem.provider_data?.users_count,
    // Series info (from Hardcover)
    series: mediaItem.provider_data?.series || null,
    // Provider info
    _provider: mediaItem.provider,
    _canonical: mediaItem
  };
}

/**
 * Convert canonical MediaItem to Listen Notes-style format for Podcasts
 */
export function toListenNotesFormat(mediaItem) {
  if (!mediaItem) return null;
  
  const lnId = mediaItem.external_ids?.listen_notes_id;
  
  return {
    id: lnId || mediaItem.id,
    listen_notes_id: lnId,
    canonical_id: mediaItem.id,
    title: mediaItem.title,
    publisher: mediaItem.subtitle || mediaItem.creators?.[0]?.name || '',
    description: mediaItem.description,
    image: mediaItem.cover_image,
    thumbnail: mediaItem.provider_data?.thumbnail || mediaItem.cover_image,
    total_episodes: mediaItem.total_episodes || mediaItem.provider_data?.total_episodes,
    genres: mediaItem.genres || [],
    listen_score: mediaItem.rating ? Math.round(mediaItem.rating * 10) : null,
    language: mediaItem.provider_data?.language,
    country: mediaItem.provider_data?.country,
    website: mediaItem.provider_data?.website,
    // Provider info
    _provider: mediaItem.provider,
    _canonical: mediaItem
  };
}

/**
 * Auto-convert based on media type
 */
export function toProviderFormat(mediaItem) {
  if (!mediaItem) return null;
  
  switch (mediaItem.type) {
    case 'tv':
    case 'movie':
      return toTMDBFormat(mediaItem);
    case 'book':
      return toGoogleBooksFormat(mediaItem);
    case 'podcast':
      return toListenNotesFormat(mediaItem);
    default:
      return mediaItem;
  }
}

/**
 * Convert array of canonical items to provider format
 */
export function toProviderFormatArray(items) {
  return items?.map(toProviderFormat) || [];
}

// ============== Hybrid Functions (use canonical API with backward compat) ==============

/**
 * Search with backward compatibility - returns provider-formatted results
 */
export async function searchMediaCompat(query, type, page = 1, limit = 24) {
  const result = await searchMedia(query, type, page, limit);
  return {
    ...result,
    items: toProviderFormatArray(result.items)
  };
}

/**
 * Discover with backward compatibility - returns provider-formatted results
 */
export async function discoverMediaCompat(type, options = {}) {
  const result = await discoverMedia(type, options);
  return {
    ...result,
    items: toProviderFormatArray(result.items)
  };
}

/**
 * Get trending with backward compatibility - returns provider-formatted results
 */
export async function getTrendingCompat(type, page = 1, timeWindow = 'week') {
  const result = await getTrending(type, page, timeWindow);
  return {
    ...result,
    items: toProviderFormatArray(result.items)
  };
}

export default {
  MediaType,
  searchMedia,
  getTrending,
  discoverMedia,
  getMediaDetails,
  getMediaDetailsFormatted,
  getTMDBDetails,
  getBookDetails,
  getPodcastDetails,
  lookupByExternalId,
  getGenres,
  toTMDBFormat,
  toTMDBDetailsFormat,
  toGoogleBooksFormat,
  toGoogleBooksDetailsFormat,
  toListenNotesFormat,
  toListenNotesDetailsFormat,
  toProviderFormat,
  toProviderFormatArray,
  searchMediaCompat,
  discoverMediaCompat,
  getTrendingCompat
};
